import { useEffect, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { getPage } from '@/services/platform/data/cms';
import {
  AppSplashScreenContentResponseModel,
  IApiContentResponseModel,
  PageSettingsPropertiesModel,
  SeoPropertiesModel,
} from '@/services/platform/api/model';
import { NotFoundError, ServerError, Splash } from '@/components/shared';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { useApp } from '@/lib/core/providers/appProvider';
import { useClientTranslation } from '@/lib/core/i18n';
import { setPageMeta, getImageUrl } from '@/lib/core/utils';
import { AppContext } from '@/lib/core/types';

export function SplashPage() {
  const locale = useLocaleStore(state => state.locale);

  const { appSettings } = useApp();
  const translation = useClientTranslation();

  const [content, setContent] = useState<IApiContentResponseModel | undefined>(undefined);
  const [context, setContext] = useState<AppContext | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchContent() {
      setLoading(true);
      setError(null);
      try {
        const data = await getPage<AppSplashScreenContentResponseModel>(
          `/${locale?.toLowerCase()?.split('-')[0]}/splashscreen`,
          locale,
          false
        );
        setContent(data);
        setContext({ page: data, settings: appSettings });
      } catch (err) {
        setError('Failed to load content');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchContent();
  }, [locale, appSettings]);

  useEffect(() => {
    const contentSEO = content?.properties as SeoPropertiesModel;
    const contentSettings = content?.properties as PageSettingsPropertiesModel;

    const contentTitle =
      contentSEO?.seoTitle ||
      `${translation('App.Labels.JNcQuoi')} - ${contentSettings?.pageName ?? content?.name}`;

    setPageMeta({
      title: contentTitle,
      description: contentSEO?.seoDescription || contentTitle,
      keywords: contentSEO?.seoKeywords || 'react, app',
      author: 'Share IT',
      ogTitle: contentTitle,
      ogDescription: contentSEO?.seoDescription || contentTitle,
      ogImage: contentSEO?.openGraphImage ? getImageUrl(contentSEO?.openGraphImage?.[0]) : undefined,
    });
  }, [content, translation]);

  if (loading) {
    return <SpinLoading wrapperClassName="min-h-[50vh]" />;
  }

  if (error) {
    //throw error;
    return <ServerError error={error} />;
  }

  if (!content) {
    return <NotFoundError />;
  }

  return <Splash content={content as AppSplashScreenContentResponseModel} context={context} />;
}
